import { getGroups } from '@api/users';
import { GroupReconfigModal } from '@components/Products/Product/Menu/BasicSettings/GroupReconfigModal';
import useToastContext from '@contexts/ToastContext/hook';
import {
  ProductScope,
  UserProduct,
  Groups,
  UserRole,
  UserRoles,
  UserCommunity,
} from '@interfaces/auth';
import { Product } from '@interfaces/product';
import { uniqueGroupsMap } from '@utils/uniqueGroupsMap';
import { VibLoadingOverlay } from '@vibTheme/components/VibLoadingOverlay';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

interface GroupsContextValue {
  groups: Groups;
  setGroups: Dispatch<SetStateAction<Groups>>;
  getAllProducts: () => UserProduct[];
  getAllProductsIds: () => number[];
  /** `false` quando a busca falhou — quem depende do estado novo precisa saber. */
  fetchGroups: () => Promise<boolean>;
  getUserRoleByProduct: (id: Product['id']) => UserRole;
  filterProductIdsByRole: (
    productIds: Array<Product['id']>,
    roles: UserRole[],
  ) => UserProduct['id'][];
  communities: UserCommunity[];
  /**
   * O escopo de relatório de cada produto alcançado, como a `platform-api` o resolveu (api#277).
   *
   * Fica aqui, e não dentro de `groups`, porque `keyof Groups` é usado como "nome de balde" —
   * `filterProductIdsByRole` indexa `groups[role]`. Vazio quando a API é anterior à api#277: nesse
   * caso o painel se comporta como antes.
   */
  escopoPorProduto: ProductScope[];
  hasNoGroups: () => boolean;
}

export const GroupsContext = createContext({} as GroupsContextValue);

const groupsInitialState = {
  owner: [],
  coproducer: [],
  affiliated: [],
  referrer: [],
  collaborator: [],
};

export function GroupsContextProvider({ children }: PropsWithChildren) {
  const [groups, setGroups] = useState<Groups>(groupsInitialState);
  const [communities, setCommunities] = useState<UserCommunity[]>([]);
  const [escopoPorProduto, setEscopoPorProduto] = useState<ProductScope[]>([]);
  const [isGroupStateReady, setIsGroupStateReady] = useState(false);
  const [showReconfigModal, setShowReconfigModal] = useState(false);
  const [groupToReconfig, setGroupToReconfig] = useState<UserProduct | null>(
    null,
  );
  const toast = useToastContext();

  const fetchGroups = async () => {
    try {
      const groups = await getGroups();
      checkReconfig(groups);

      // Remove registros de afiliação duplicados quando o usuário já é coprodutor
      groups.affiliated = groups.affiliated.filter(
        (affiliationGroup) =>
          !groups.coproducer.find(
            (coproductionGroup) => coproductionGroup.id === affiliationGroup.id,
          ),
      );

      /*
        DEPOIS da linha acima, e não antes.

        `formatCommunities` percorre os baldes, então rodá-lo antes da normalização fazia a mesma
        comunidade sair com `['COPRODUCER', 'AFFILIATED']` — justamente o registro de afiliação que a
        linha acima acabou de considerar duplicado. E o papel ali não é só rótulo: os seletores
        recortam por ele, então `salesAsAffiliate`, que pede `AFFILIATED`, passaria a listar uma
        comunidade que o balde de afiliação não contém.
      */
      const communities = formatCommunities(groups);
      setCommunities(communities);
      /*
        `?? []` porque a resposta pode vir de uma `platform-api` anterior à api#277, na janela de
        deploy. Vazio é tratado como "não sei" por quem consome, e não como "nenhum escopo".
      */
      setEscopoPorProduto(groups.reportScopeByProduct ?? []);

      setGroups(groups);
      setIsGroupStateReady(true);

      return true;
    } catch (err) {
      toast.handleError(
        err,
        'Ocorreu um erro ao buscar os dados de suas comunidades. Por favor, entre em contato com o suporte.',
      );

      /*
        Devolve FALSO em vez de só engolir. O efeito colateral do erro — o toast — basta para a
        montagem inicial, mas não para quem chama isto esperando estado novo: o aceite de convite
        anuncia sucesso e manda a pessoa para dentro do app, e com os grupos velhos a comunidade
        recém-aceita não está lá e a rota dela cai em `/404`.
      */
      return false;
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  if (!isGroupStateReady) return <VibLoadingOverlay />;

  function getAllProducts() {
    /*
      A colaboração entra aqui porque este é o conjunto de "comunidades que a pessoa ALCANÇA" — é o
      que alimenta o seletor de produto e a checagem do `CurrentProductContext`. Sem ela, quem aceita
      um convite entra num app onde a comunidade não existe, e a rota dela cai em `/404`.

      O que NÃO muda: `getUserRoleByProduct` segue conhecendo só os quatro papéis de comissão, e os
      filtros por papel seguem sem colaboração até a migração da §6.3. Alcance e papel são coisas
      diferentes, e misturá-los aqui daria ao colaborador as permissões de afiliado pelo default.
    */
    const comVinculo = [
      ...groups.owner,
      ...groups.coproducer,
      ...groups.affiliated,
    ];

    /*
      A colaboração entra só onde AINDA NÃO HÁ o produto, e a deduplicação tem de ser por ID.

      `uniqueGroupsMap` compara objetos serializados, e o mesmo produto vindo de dois baldes traz
      `userRole` diferente — então as duas cópias sobreviveriam, duplicando a entrada no seletor e
      repetindo o id em requisição multi-produto. Quem é colaborador E tem papel de comissão no mesmo
      produto é o caso: acontece quando o papel é criado depois da colaboração.

      O vínculo é quem fica, e não a colaboração: é dele que sai o `userRole` que o resto do app lê.
    */
    const idsComVinculo = new Set(comVinculo.map((product) => product.id));

    return uniqueGroupsMap([
      ...comVinculo,
      ...groups.collaborator.filter(
        (product) => !idsComVinculo.has(product.id),
      ),
    ]);
  }

  function getAllProductsIds() {
    const allProducts = getAllProducts();
    return allProducts.map((product) => product.id);
  }

  function getUserRoleByProduct(id: Product['id']): UserRole {
    const areProductIdsEqual = (product: UserProduct) => product.id === id;

    if (groups.owner.find(areProductIdsEqual)) return 'OWNER';
    if (groups.coproducer.find(areProductIdsEqual)) return 'COPRODUCER';
    if (groups.affiliated.find(areProductIdsEqual)) return 'AFFILIATED';
    if (groups.referrer.find(areProductIdsEqual)) return 'REFERRER';

    /*
      Colaboração vem DEPOIS dos quatro de comissão, e antes do default.

      A ordem é o que importa: quem colabora e também tem papel resolve pelo papel, como sempre. Quem
      SÓ colabora precisa parar aqui — cair no `AFFILIATED` de baixo lhe daria as permissões de
      afiliado sem grant nenhum, que é exatamente o furo que a resolução por permissão efetiva existe
      para fechar (e que a platform-api já fecha do lado do servidor).

      `COLLABORATOR` não tem posição no bitmask de três casas, então `parseFeaturePermission` nunca o
      devolve: a matriz não concede nada a ele. A tela falha FECHADA até o mapa de permissão efetiva
      por produto existir (§6.2) — o que ele já podia ver, ele continua vendo pelo papel; o que foi
      concedido no grant aparece quando aquele mapa chegar.
    */
    if (groups.collaborator.find(areProductIdsEqual)) return 'COLLABORATOR';

    return 'AFFILIATED';
  }

  function filterProductIdsByRole(
    productIds: Array<Product['id']>,
    roles: UserRole[],
  ) {
    const lowercasedRoles = roles.map((role) =>
      Object.values(UserRoles)
        .find((value) => value === role)
        ?.toLowerCase(),
    ) as Array<keyof Groups>;

    const groupIds = lowercasedRoles.map((role) =>
      productIds.filter((groupId) =>
        groups[role].find((group) => group.id === groupId),
      ),
    );

    const uniqueProductIds = [...new Set(groupIds.flat())];
    return uniqueProductIds;
  }

  /**
   * O papel de cada comunidade sai do BALDE em que ela veio, e não do `userRole` da linha.
   *
   * O balde de colaboração chega da API com `userRole: 'AFFILIATED'`, e é preenchimento declarado:
   * o `UserRole` de lá tem só os quatro papéis de comissão, e a própria API documenta que ninguém
   * lê aquele campo para decidir permissão. Só que ISTO lia — e o valor virava rótulo e virava
   * recorte. O efeito na tela era duplo: a comunidade de colaboração aparecia no seletor do painel
   * escrita como "Afiliado", e só aparecia com "Afiliado" escolhido, que pede à API a comissão de
   * quem não tem comissão nenhuma ali.
   *
   * O balde é a informação que este contexto já tem, de primeira mão. Usá-lo elimina a dependência
   * de um campo que a API chama de irrelevante — e, de graça, passa a expressar quem é as duas
   * coisas: afiliada E colaboradora da mesma comunidade agora sai com os dois papéis, onde antes as
   * duas linhas diziam "AFFILIATED" e a deduplicação as fundia numa.
   */
  function formatCommunities(groups: Groups): UserCommunity[] {
    const userCommunities: UserCommunity[] = [];
    const baldes: [UserProduct[], UserRole][] = [
      [groups.owner, UserRoles.OWNER],
      [groups.coproducer, UserRoles.COPRODUCER],
      [groups.affiliated, UserRoles.AFFILIATED],
      [groups.referrer, UserRoles.REFERRER],
      [groups.collaborator, UserRoles.COLLABORATOR],
    ];

    baldes.forEach(([produtos, papel]) =>
      produtos.forEach((group) => {
        const communityIdx = userCommunities.findIndex(
          (newGroup) => newGroup.id === group.id,
        );
        communityIdx === -1
          ? userCommunities.push({ ...group, userRoles: [papel] })
          : userCommunities[communityIdx].userRoles.push(papel);
      }),
    );

    return userCommunities;
  }

  function hasNoGroups() {
    return getAllProductsIds.length === 0;
  }

  function checkReconfig(groups: Groups) {
    if (!groups || !groups?.owner || !groups?.coproducer) return;

    [...groups.owner, ...groups.coproducer].forEach((group) => {
      const shouldReconfig =
        (group.botMonitoring.leftOrKicked &&
          group.botMonitoring.enableLeftOrKickedAlert) ||
        (group.botMonitoring.withoutCommunication &&
          group.botMonitoring.enableWithoutCommunicationAlert) ||
        (group.botMonitoring.withoutPermissions &&
          group.botMonitoring.enableWithoutPermissionsAlert);

      if (shouldReconfig) {
        setShowReconfigModal(true);
        setGroupToReconfig(group);
        return;
      }
    });
  }

  return (
    <GroupsContext.Provider
      value={{
        groups,
        setGroups,
        getAllProducts,
        getAllProductsIds,
        fetchGroups,
        getUserRoleByProduct,
        filterProductIdsByRole,
        communities,
        escopoPorProduto,
        hasNoGroups,
      }}
    >
      <GroupReconfigModal
        group={groupToReconfig}
        isOpen={showReconfigModal}
        setIsOpen={setShowReconfigModal}
      />
      {children}
    </GroupsContext.Provider>
  );
}
